import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import equal from "fast-deep-equal/es6";
import { denormalize } from "normalizr";
import "../../api/types.js";
import { toSearchFilter, getFilterString, getPagingString, appendQuery } from "../../api/utils.js";
import "immer";
import "moment-timezone";
import "react-intl";
import "@apollo/client/errors";
import "rxjs";
import "rxjs/operators";
import "@apollo/client";
import "graphql";
import "@opentelemetry/api";
import "../../otel/TraceProvider.js";
import "react-error-boundary";
import "@opentelemetry/context-zone";
import "@opentelemetry/exporter-trace-otlp-http";
import "@opentelemetry/instrumentation";
import "@opentelemetry/instrumentation-document-load";
import "@opentelemetry/instrumentation-xml-http-request";
import "@opentelemetry/resources";
import "@opentelemetry/sdk-trace-web";
import "@opentelemetry/semantic-conventions";
import "@apollo/client/link/subscriptions";
import "@apollo/client/utilities";
import "cross-fetch";
import "graphql-ws";
import "../../node_modules/moment/dist/moment.js";
import "rxjs/ajax";
import "redux";
import "typesafe-actions";
import "../entity/epics.js";
import { entitiesSelectorWithoutReferences } from "../entity/selectors.js";
import "../entity/EntityProvider.js";
import { useSchema } from "../entity/hooks.js";
import { getViewActions } from "./actions.js";
import { createViewEntityKeysSelector, createViewEntityIsInitializedSelector, createViewEntityTotalSelector, createViewEntityRequestStatusSelector, createViewEntityFilterSelector } from "./selectors.js";
import { schemaIdKey } from "./types.js";
import { mapSortingToFilter } from "./utils.js";
function useRealtime(viewType, realtime) {
  const viewActions = getViewActions();
  const dispatch = useDispatch();
  const [prevTriggers, setPrevTriggers] = useState(realtime);
  useEffect(() => {
    if (realtime) {
      if (JSON.stringify(prevTriggers) !== JSON.stringify(realtime)) {
        dispatch(viewActions.refreshSubscription({ view: viewType, realtime }));
        setPrevTriggers(realtime);
      }
    }
  }, [JSON.stringify(realtime)]);
}
function useRealtimePauseResume() {
  const viewActions = getViewActions();
  const dispatch = useDispatch();
  function pause() {
    dispatch(viewActions.pause());
  }
  function resume() {
    dispatch(viewActions.resume());
  }
  return {
    pause,
    resume
  };
}
const useViewInit = ({
  viewType,
  displayName,
  entity,
  realtime,
  defaultFilters,
  defaultSorting,
  defaultPaging,
  hiddenFilters,
  syncWithUrl = false,
  validateFilter,
  cleanDelay = 0,
  blockFetchWithInvalidFilter = false,
  isCancelable
}) => {
  const dispatch = useDispatch();
  const keys = useSelector(
    (state) => createViewEntityKeysSelector({ entityType: entity.entity, viewType })(state)
  ) ?? [];
  const entitiesState = useSelector((state) => entitiesSelectorWithoutReferences(state));
  const isViewInitialized = useSelector(
    (state) => createViewEntityIsInitializedSelector({ viewType, entityType: entity.entity })(state)
  );
  const schemaMapper = useSchema();
  const schema = schemaMapper.getSchema(entity.entity);
  const entities = addSchemaIdsToEntitiesItems(entitiesState);
  const items = denormalize(keys, [schema], entities);
  const totalCount = useSelector(
    (state) => createViewEntityTotalSelector({ entityType: entity.entity, viewType })(state)
  );
  const requestStatus = useSelector(
    (state) => createViewEntityRequestStatusSelector({ entityType: entity.entity, viewType })(state)
  );
  const [defaultFilterValues, setDefaultFilters] = useState(defaultFilters);
  const stateFilter = useSelector(
    (state) => createViewEntityFilterSelector({ viewType, entityType: entity.entity })(state)
  );
  const searchFilter = toSearchFilter(stateFilter);
  const viewActions = getViewActions();
  useRealtime(viewType, realtime);
  const [isViewMounted, setIsViewMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    setIsViewMounted(true);
  }, []);
  useEffect(() => {
    const isViewReadyForInit = viewType !== null && viewType !== void 0 && isViewMounted;
    if (isViewReadyForInit) {
      const initialFilter = getInitialFilter();
      dispatch(
        viewActions.init({
          view: viewType,
          entity: { ...entity, keys: [], total: 0, status: "inProgress" },
          realtime,
          displayName,
          filter: initialFilter,
          blockFetchWithInvalidFilter,
          cleanDelay,
          isCancelable
        })
      );
      syncUrlWithFilter(initialFilter, "replace");
    }
    return () => {
      if (isViewReadyForInit) {
        dispatch(viewActions.close({ view: viewType }));
      }
    };
  }, [isViewMounted]);
  useEffect(() => {
    if (isViewInitialized && defaultFilters && defaultFilters?.length && !equal(defaultFilterValues, defaultFilters)) {
      handleFilterChange(defaultFilters);
      setDefaultFilters(defaultFilters);
    }
  }, [
    isViewInitialized,
    defaultFilters?.map((f) => `${f?.key}${JSON.stringify(f?.value)}`).join(),
    defaultPaging?.page,
    defaultPaging?.pageSize
  ]);
  const prevHiddenFilters = useRef(null);
  useEffect(() => {
    if (isViewInitialized) {
      if (prevHiddenFilters.current !== null && !equal(prevHiddenFilters.current, hiddenFilters)) {
        handleFilterChange(hiddenFilters);
      }
      prevHiddenFilters.current = hiddenFilters;
    }
  }, [isViewInitialized, hiddenFilters?.map((f) => `${f?.key}${JSON.stringify(f?.value)}`).join()]);
  function getInitialFilter() {
    let initialFilterString = "";
    if (syncWithUrl && location.search?.length > 0) {
      initialFilterString = getFilterString(location.search, false, ...hiddenFilters ?? []);
    } else if (stateFilter) {
      initialFilterString = stateFilter;
    } else {
      const sortingFilters = convertSortingFromArrayToObject(defaultSorting);
      initialFilterString = syncFilter(initialFilterString, {
        filters: defaultFilters,
        sorting: sortingFilters,
        paging: defaultPaging
      });
    }
    const validatedFilter = getValidatedFilter(initialFilterString);
    return validatedFilter;
  }
  function sortFilterParams(filter) {
    const searchParams = new URLSearchParams(filter ?? "");
    searchParams.sort();
    return searchParams.toString();
  }
  function syncFilter(initialFilterString, filterModel) {
    let updatedFilterString = initialFilterString;
    if (filterModel?.filters?.length > 0) {
      updatedFilterString = getFilterString(updatedFilterString, false, ...filterModel.filters.filter((i) => i));
    }
    if (filterModel?.sorting) {
      const sortingFilters = mapSortingToFilter(filterModel.sorting);
      updatedFilterString = getFilterString(updatedFilterString, false, ...sortingFilters.filter((i) => i) ?? []);
    }
    if (filterModel?.paging?.page > 0 || filterModel?.paging?.pageSize > 0) {
      updatedFilterString = getPagingString(updatedFilterString, filterModel.paging);
    }
    if (hiddenFilters?.length > 0) {
      updatedFilterString = getFilterString(updatedFilterString, false, ...hiddenFilters.filter((i) => i));
    }
    return sortFilterParams(updatedFilterString) ?? "";
  }
  function getValidatedFilter(filter) {
    let result = filter;
    const searchFilter2 = toSearchFilter(filter);
    if (validateFilter) {
      const invalidKey = "invalid";
      const searchParams = new URLSearchParams(result);
      const isValid = validateFilter(searchFilter2.filter);
      if (!isValid) {
        searchParams.set(invalidKey, "true");
      }
      if (isValid && searchParams.has(invalidKey)) {
        searchParams.delete(invalidKey);
      }
      result = searchParams.toString();
    }
    return result;
  }
  function handleFilterChange(filters) {
    const filterString = syncFilter(stateFilter, { filters, paging: defaultPaging ?? { page: 1 } });
    updateFilter(filterString);
  }
  function handlePageChange(newPage) {
    const { pageSize } = searchFilter.paging;
    const newPaging = { page: newPage, pageSize };
    const newFilter = syncFilter(stateFilter, { paging: newPaging });
    updateFilter(newFilter);
  }
  function handlePageSizeChange(newPageSize) {
    const { page, pageSize } = searchFilter.paging;
    const getNewPageNumber = (rowsPerPage) => {
      const firstPageItemIndex = pageSize * (page - 1) + 1;
      const newPage2 = Math.ceil(firstPageItemIndex / rowsPerPage);
      return newPage2;
    };
    const newPage = getNewPageNumber(newPageSize);
    const newPaging = { page: newPage, pageSize: newPageSize };
    const newFilter = syncFilter(stateFilter, { paging: newPaging });
    updateFilter(newFilter);
  }
  function handleSortChange(newSorting) {
    const newSortingObj = convertSortingFromArrayToObject(newSorting);
    const newFilter = syncFilter(stateFilter, { sorting: newSortingObj });
    updateFilter(newFilter);
  }
  function updateFilter(filter) {
    const resultFilter = getValidatedFilter(filter);
    if (resultFilter !== stateFilter) {
      dispatch(
        viewActions.updateFilter({
          entity: entity.entity,
          view: viewType,
          filter: resultFilter,
          isCancelable,
          blockFetchWithInvalidFilter
        })
      );
      syncUrlWithFilter(resultFilter, "push");
    }
  }
  function getQueryStringByFilter(filter) {
    const searchParams = new URLSearchParams(filter ?? "");
    const invalidFilterKey = "invalid";
    if (searchParams.has(invalidFilterKey)) {
      searchParams.delete(invalidFilterKey);
    }
    hiddenFilters?.forEach((f) => {
      if (searchParams.has(f.key)) {
        searchParams.delete(f.key);
      }
    });
    return searchParams.toString();
  }
  function syncUrlWithFilter(filter, action) {
    const normalizedLocationSearch = location.search.replace(/^\?/, "");
    const filterQueryString = getQueryStringByFilter(filter);
    if (syncWithUrl && filterQueryString !== normalizedLocationSearch) {
      const newUrl = appendQuery(location.pathname, filterQueryString);
      if (action === "push") {
        navigate(newUrl);
      } else {
        navigate(newUrl, { replace: true });
      }
    }
  }
  function addSchemaIdsToEntitiesItems(entities2) {
    const result = { ...entities2 ?? {} };
    Object.keys(result).forEach((entityType) => {
      const itemsWithSchemaIds = Object.entries(entities2[entityType])?.reduce(
        (previousValue, [key, item]) => {
          previousValue[key] = { ...item, [schemaIdKey]: key };
          return previousValue;
        },
        {}
      );
      result[entityType] = itemsWithSchemaIds;
    });
    return result;
  }
  function convertSortingFromArrayToObject(sorting) {
    let res = void 0;
    if (sorting && sorting.length > 0) {
      const fields = sorting.map((s) => s.field).join();
      const sort = sorting.map((s) => s.sort).join();
      res = { field: fields, sort };
    }
    return res;
  }
  function getSearchFilter(mapper) {
    let res = searchFilter;
    if (searchFilter?.sorting?.length > 0) {
      res = { ...res, sorting: searchFilter?.sorting?.map((s) => ({ field: mapper(s.field), sort: s.sort })) };
    }
    return res;
  }
  function forceViewUpdate() {
    if (isViewInitialized) {
      dispatch(viewActions.update({ views: [viewType], entity: entity.entity }));
    }
  }
  const defaultFilterString = defaultFilters && defaultFilters.length > 0 ? getFilterString("", false, ...defaultFilters) : "";
  return {
    items,
    totalCount,
    searchFilter,
    viewEntity: {
      status: requestStatus,
      entity: entity.entity,
      fields: entity.fields,
      filter: stateFilter
    },
    isDataLoading: requestStatus === "inProgress",
    handleFilterChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    getSearchFilter,
    forceViewUpdate,
    defaultFilterString
  };
};
function useView(view) {
  const dispatch = useDispatch();
  const viewActions = getViewActions();
  function forceUpdateFilter(entity, filter) {
    dispatch(viewActions.updateFilter({ view, entity, filter }));
  }
  return {
    forceUpdateFilter
  };
}
export {
  useRealtimePauseResume,
  useView,
  useViewInit
};
//# sourceMappingURL=hooks.js.map
